import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { BrowserMultiFormatReader, NotFoundException } from "@zxing/library";
import { ArrowLeft, Camera, CheckCircle2, Circle, ExternalLink, Play, ScanLine, Square, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Administrator-only diagnostic page for camera / barcode scanning.
 * Impostazioni → Diagnostica dispositivo → Fotocamera
 */
export default function CameraDiagnostics() {
  const { hasRole } = useAuth();
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const readerRef = useRef(null);

  const [permission, setPermission] = useState("unknown");
  const [devices, setDevices] = useState([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState("");
  const [running, setRunning] = useState(false);
  const [snapshot, setSnapshot] = useState(null);
  const [errorCode, setErrorCode] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [scanResult, setScanResult] = useState(null);
  const [ua] = useState(typeof navigator !== "undefined" ? navigator.userAgent : "");

  const isSecure = typeof window !== "undefined" ? window.isSecureContext : false;
  const inIframe = typeof window !== "undefined" ? window.self !== window.top : false;
  const hasMediaDevices = typeof navigator !== "undefined" && !!navigator.mediaDevices;
  const hasGetUserMedia = hasMediaDevices && typeof navigator.mediaDevices.getUserMedia === "function";
  const hasPermissionsApi = typeof navigator !== "undefined" && !!navigator.permissions && !!navigator.permissions.query;

  const refreshPermission = useCallback(async () => {
    if (!hasPermissionsApi) { setPermission("unavailable"); return; }
    try {
      const r = await navigator.permissions.query({ name: "camera" });
      setPermission(r.state);
      r.onchange = () => setPermission(r.state);
    } catch { setPermission("unavailable"); }
  }, [hasPermissionsApi]);

  useEffect(() => { refreshPermission(); }, [refreshPermission]);

  const enumerate = useCallback(async () => {
    if (!hasMediaDevices || !navigator.mediaDevices.enumerateDevices) return;
    try {
      const list = (await navigator.mediaDevices.enumerateDevices()).filter((d) => d.kind === "videoinput");
      setDevices(list);
      if (!selectedDeviceId && list[0]) setSelectedDeviceId(list[0].deviceId);
    } catch (e) { setErrorCode(e?.name || "Error"); setErrorMsg(e?.message || ""); }
  }, [hasMediaDevices, selectedDeviceId]);

  const stopCamera = useCallback(() => {
    try { readerRef.current?.reset(); } catch (_) { /* no-op */ }
    readerRef.current = null;
    const s = streamRef.current;
    if (s) { s.getTracks().forEach((t) => { try { t.stop(); } catch (_) { /* no-op */ } }); streamRef.current = null; }
    if (videoRef.current) { try { videoRef.current.srcObject = null; } catch (_) { /* no-op */ } }
    setRunning(false);
  }, []);

  const startCamera = useCallback(async (constraints) => {
    setErrorCode(""); setErrorMsg(""); setScanResult(null);
    if (!isSecure) { setErrorCode("SecurityError"); setErrorMsg("Contesto non sicuro (HTTPS)."); return; }
    if (inIframe) { setErrorCode("IframePermissionBlocked"); setErrorMsg("Aperto in iframe — i permessi fotocamera potrebbero essere bloccati."); }
    if (!hasGetUserMedia) { setErrorCode("UnsupportedBrowser"); setErrorMsg("getUserMedia non disponibile."); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      const v = videoRef.current;
      v.srcObject = stream;
      v.setAttribute("playsinline", "true");
      v.muted = true;
      await new Promise((resolve) => { if (v.readyState >= 2) return resolve(); v.onloadedmetadata = () => resolve(); });
      try { await v.play(); } catch (_) { /* no-op */ }
      setRunning(true);
      await enumerate();
    } catch (e) {
      setErrorCode(e?.name || "Error"); setErrorMsg(e?.message || "");
    }
  }, [isSecure, inIframe, hasGetUserMedia, enumerate]);

  useEffect(() => () => stopCamera(), [stopCamera]);

  const requestPermission = () => startCamera({ video: true, audio: false });
  const startRear = () => startCamera({ video: { facingMode: { ideal: "environment" } }, audio: false });
  const startSelected = () => selectedDeviceId ? startCamera({ video: { deviceId: { exact: selectedDeviceId } }, audio: false }) : startCamera({ video: true, audio: false });

  const takeSnapshot = () => {
    const v = videoRef.current;
    if (!v || !running) return;
    const canvas = document.createElement("canvas");
    canvas.width = v.videoWidth; canvas.height = v.videoHeight;
    canvas.getContext("2d").drawImage(v, 0, 0);
    setSnapshot(canvas.toDataURL("image/jpeg", 0.85));
  };

  const testZxing = () => {
    const v = videoRef.current;
    if (!v || !running) return;
    setScanResult(null);
    try { readerRef.current?.reset(); } catch (_) { /* no-op */ }
    const reader = new BrowserMultiFormatReader();
    readerRef.current = reader;
    reader.decodeFromVideoElement(v, (result, err) => {
      if (result) {
        try { reader.reset(); } catch (_) { /* no-op */ }
        setScanResult({ text: result.getText(), format: result.getBarcodeFormat?.() || "" });
      }
      if (err && !(err instanceof NotFoundException)) {
        // ignore
      }
    });
  };

  if (!hasRole("administrator")) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <Alert variant="destructive"><AlertTitle>Accesso riservato</AlertTitle><AlertDescription>Questa pagina è disponibile solo agli amministratori.</AlertDescription></Alert>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 max-w-4xl mx-auto space-y-4" data-testid="camera-diagnostics-page">
      <Link to="/impostazioni" className="text-sm text-slate-500 inline-flex items-center gap-1 hover:text-[#d63384]"><ArrowLeft className="w-4 h-4" /> Impostazioni</Link>
      <div>
        <div className="text-xs uppercase tracking-widest text-[#d63384] font-semibold">Diagnostica dispositivo</div>
        <h1 className="text-2xl md:text-3xl font-heading font-bold text-[#0a0a2e]">Fotocamera</h1>
        <p className="text-sm text-slate-500">Verifica ambiente browser, permessi e stream video prima di usare lo scanner in produzione.</p>
      </div>

      <Card className="ama-card border-0 shadow-none">
        <CardHeader><CardTitle className="font-heading text-lg">Ambiente</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
          <Row k="Contesto sicuro (HTTPS)" v={isSecure} data-testid="diag-secure" />
          <Row k="In iframe" v={inIframe} inverse data-testid="diag-iframe" />
          <Row k="getUserMedia disponibile" v={hasGetUserMedia} data-testid="diag-gum" />
          <Row k="Permissions API" v={hasPermissionsApi} data-testid="diag-perm-api" />
          <div className="col-span-full">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Permesso fotocamera</div>
            <Badge variant="outline" data-testid="diag-permission-state">{permission}</Badge>
          </div>
          <div className="col-span-full">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">User-Agent</div>
            <div className="text-[11px] font-mono text-slate-600 break-all" data-testid="diag-ua">{ua}</div>
          </div>
        </CardContent>
      </Card>

      <Card className="ama-card border-0 shadow-none">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="font-heading text-lg">Azioni</CardTitle>
          {inIframe && (
            <Button variant="outline" size="sm" onClick={() => window.open(window.location.href, "_blank", "noopener,noreferrer")}><ExternalLink className="w-4 h-4 mr-1" /> Apri in nuova scheda</Button>
          )}
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-2">
            <Button data-testid="diag-request-permission" onClick={requestPermission}><Camera className="w-4 h-4 mr-1" /> Richiedi autorizzazione</Button>
            <Button data-testid="diag-start-rear" variant="outline" onClick={startRear}><Play className="w-4 h-4 mr-1" /> Prova fotocamera posteriore</Button>
            <Button data-testid="diag-start-selected" variant="outline" onClick={startSelected}><Play className="w-4 h-4 mr-1" /> Avvia fotocamera selezionata</Button>
            <Button data-testid="diag-stop" variant="outline" onClick={stopCamera}><Square className="w-4 h-4 mr-1" /> Ferma fotocamera</Button>
            <Button data-testid="diag-snapshot" variant="outline" onClick={takeSnapshot} disabled={!running}><Circle className="w-4 h-4 mr-1" /> Scatta foto di prova</Button>
            <Button data-testid="diag-scan" variant="outline" onClick={testZxing} disabled={!running}><ScanLine className="w-4 h-4 mr-1" /> Testa lettura codice</Button>
          </div>

          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Fotocamere rilevate ({devices.length})</div>
            {devices.length === 0 ? (
              <div className="text-sm text-slate-500 italic">Nessuna. Concedi il permesso e premi &laquo;Richiedi autorizzazione&raquo;.</div>
            ) : (
              <div className="grid gap-1 text-xs">
                {devices.map((d) => (
                  <label key={d.deviceId} className="flex items-center gap-2 p-2 border border-slate-200 rounded-md">
                    <input type="radio" name="cam" checked={selectedDeviceId === d.deviceId} onChange={() => setSelectedDeviceId(d.deviceId)} />
                    <span className="font-mono flex-1 truncate">{d.label || "(nessuna etichetta)"}</span>
                    <span className="text-slate-400">{d.deviceId.slice(0, 8)}…</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          {errorCode && (
            <Alert variant="destructive" data-testid="diag-error"><XCircle className="w-4 h-4" /><AlertTitle>Errore: {errorCode}</AlertTitle><AlertDescription>{errorMsg}</AlertDescription></Alert>
          )}

          <div className="rounded-xl overflow-hidden bg-black relative">
            <video ref={videoRef} className="w-full aspect-video bg-black object-cover" autoPlay playsInline muted data-testid="diag-video" />
            {!running && <div className="absolute inset-0 flex items-center justify-center text-white/70 text-sm">Nessun stream attivo</div>}
          </div>

          {scanResult && (
            <Alert data-testid="diag-scan-result"><CheckCircle2 className="w-4 h-4" /><AlertTitle>Codice rilevato</AlertTitle><AlertDescription><span className="font-mono">{scanResult.text}</span> · {scanResult.format}</AlertDescription></Alert>
          )}

          {snapshot && (
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Anteprima scatto (non salvato)</div>
              <img src={snapshot} alt="snapshot" className="max-w-xs rounded-lg border" />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Row({ k, v, inverse = false, "data-testid": testId }) {
  const good = inverse ? !v : v;
  return (
    <div className="flex items-center justify-between gap-2 border-b border-slate-100 py-1" data-testid={testId}>
      <span className="text-slate-600">{k}</span>
      <span className={`inline-flex items-center gap-1 text-xs font-semibold ${good ? "text-emerald-600" : "text-rose-600"}`}>
        {good ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
        {v ? "Sì" : "No"}
      </span>
    </div>
  );
}
